import React from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { captureException } from '@sentry/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config';
import { useAuth, useUserDataContext, useDuplicateFlags } from '../../../../hooks/index.js';
import { usePatientIntakeStatusContext } from '../hooks/index.ts';
import { updateIntakeStatus } from './updateIntakeStatus.ts';

const RESOLUTIONS = {
  FALSE_POSITIVE: 'FALSE_POSITIVE',
  CONFIRMED_DUPLICATE: 'CONFIRMED_DUPLICATE',
} as const;

const validationSchema = z
  .object({
    resolution: z.enum([RESOLUTIONS.FALSE_POSITIVE, RESOLUTIONS.CONFIRMED_DUPLICATE]),
    primaryAccountId: z.string().optional(),
    resolutionNote: z.string().min(1, 'A resolution note is required'),
  })
  .refine(
    (data) => {
      if (data.resolution === RESOLUTIONS.CONFIRMED_DUPLICATE && !data.primaryAccountId) {
        return false;
      }
      return true;
    },
    { message: 'Please select the primary account', path: ['primaryAccountId'] },
  );

type AccountVerificationFormData = z.infer<typeof validationSchema>;

type AccountVerificationResolutionFormProps = {
  step: IntakeStatusBaseSteps['ACCOUNT_VERIFICATION'];
  onSuccess: () => void;
  onClose: () => void;
};

export const AccountVerificationResolutionForm: React.FC<AccountVerificationResolutionFormProps> = ({
  step,
  onSuccess,
  onClose,
}) => {
  const { user, userType } = useAuth();
  const { userData } = useUserDataContext();
  const { patientId } = usePatientIntakeStatusContext();
  const { data: duplicateFlags } = useDuplicateFlags(patientId);

  const candidates = duplicateFlags.map((flag) => {
    const otherPatientId = flag.patientIds.find((id) => id !== patientId) ?? flag.patientIds[0];
    const otherPatient = flag.patients[otherPatientId];
    return {
      id: otherPatientId,
      name: otherPatient ? `${otherPatient.firstName} ${otherPatient.lastName}` : otherPatientId,
      email: otherPatient?.email ?? '',
    };
  });

  const methods = useForm<AccountVerificationFormData>({
    defaultValues: {
      resolution: RESOLUTIONS.FALSE_POSITIVE,
      primaryAccountId: candidates.length === 1 ? candidates[0]?.id : '',
      resolutionNote: '',
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
    setError,
  } = methods;

  const resolution = watch('resolution');

  const handleOnSubmit = async (data: AccountVerificationFormData) => {
    const isConfirmedDuplicate = data.resolution === RESOLUTIONS.CONFIRMED_DUPLICATE;

    const selectedPrimaryAccountId = isConfirmedDuplicate
      ? data.primaryAccountId || candidates[0]?.id
      : undefined;

    if (isConfirmedDuplicate && !selectedPrimaryAccountId) {
      setError('primaryAccountId', {
        type: 'manual',
        message: 'No duplicate candidates are available to select as a primary account.',
      });
      toast.error('There are no duplicate candidates to resolve. Please close this dialog.');
      return;
    }

    try {
      const primaryAccountId = selectedPrimaryAccountId;

      await updateIntakeStatus({
        user,
        userData,
        userType,
        patientId,
        step,
        status: 'COMPLETED',
        notes: data.resolutionNote,
        isNoteVisibleToPatient: false,
        isMedicalNote: false,
        metadataOverride: {
          resolution: data.resolution,
          resolutionNote: data.resolutionNote,
          ...(primaryAccountId ? { primaryAccountId } : {}),
        },
      });

      // Mark unresolved duplicate flags for this patient as resolved
      const unresolvedFlags = duplicateFlags.filter((flag) => !flag.resolvedAt);
      await Promise.all(
        unresolvedFlags.map((flag) =>
          updateDoc(doc(db, 'duplicateFlags', flag.id), {
            resolution: data.resolution,
            resolvedAt: serverTimestamp(),
          }),
        ),
      );

      toast.success('Resolution saved successfully');
      onSuccess();
      onClose();
    } catch (error) {
      captureException(error, {
        tags: { issueIn: 'AccountVerificationResolutionForm.handleOnSubmit' },
      });

      setError('root', {
        type: 'manual',
        message: 'Failed to save resolution. Please try again.',
      });

      toast.error('Failed to save resolution');
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack gap={2}>
        {errors.root && (
          <Alert severity="error">{errors.root.message}</Alert>
        )}

        <Controller
          name="resolution"
          control={control}
          render={({ field }) => (
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <Typography variant="subtitle2">Resolution</Typography>
              </FormLabel>
              <RadioGroup {...field}>
                <FormControlLabel
                  value={RESOLUTIONS.FALSE_POSITIVE}
                  control={<Radio size="small" />}
                  label={
                    <Stack>
                      <Typography variant="body2">False Positive</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Both accounts are legitimate. No action needed.
                      </Typography>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value={RESOLUTIONS.CONFIRMED_DUPLICATE}
                  control={<Radio size="small" />}
                  label={
                    <Stack>
                      <Typography variant="body2">Confirmed Duplicate</Typography>
                      <Typography variant="caption" color="text.secondary">
                        This account is a duplicate. The account should be disabled.
                      </Typography>
                    </Stack>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}
        />

        {resolution === RESOLUTIONS.CONFIRMED_DUPLICATE && candidates.length > 0 && (
          <Controller
            name="primaryAccountId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error} size="small">
                <Typography variant="subtitle2" gutterBottom>
                  Primary Account
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  Select which candidate is the legitimate (primary) account.
                </Typography>
                <Select {...field} displayEmpty>
                  <MenuItem value="" disabled>
                    Select primary account
                  </MenuItem>
                  {candidates.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {error.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        )}

        <Controller
          name="resolutionNote"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Resolution Note"
              required
              multiline
              rows={3}
              fullWidth
              size="small"
              error={!!error}
              helperText={error?.message || 'Required. Explain the reasoning for this resolution.'}
            />
          )}
        />
      </Stack>

      <DialogActions sx={{ px: 0, mt: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleOnSubmit)}
          variant="contained"
          color="primary"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
        >
          Resolve
        </Button>
      </DialogActions>
    </FormProvider>
  );
};
